import { useState, useEffect } from "react";
import { supabase } from "@/services/supabase";
import { useNavigate } from "react-router-dom";
import { PASSWORD_MIN_LENGTH, WEB_AUTH_UI_ENABLED } from "@/config/publicConfig";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<
    "initializing" | "ready" | "expired" | "error"
  >("initializing");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        // Supabase recovery links usually carry tokens in the URL hash.
        // Example: #access_token=...&refresh_token=...&type=recovery
        const hash = window.location.hash?.replace(/^#/, "") ?? "";
        const params = new URLSearchParams(hash);
        const type = params.get("type") || params.get("token_type");
        const accessToken = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        const hashError =
          params.get("error_description") || params.get("error") || "";

        if (hashError) {
          if (cancelled) return;
          setStatus("error");
          setErrorMsg(decodeURIComponent(hashError));
          return;
        }

        if (type === "recovery" && accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;

          if (cancelled) return;
          setStatus("ready");
          return;
        }

        // Fallback: maybe user is already signed in (e.g. opened from same browser)
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (cancelled) return;
        if (session) {
          setStatus("ready");
        } else {
          setStatus("expired");
        }
      } catch (e: any) {
        if (cancelled) return;
        setStatus("error");
        setErrorMsg(e?.message || "初始化失败，请重新申请重置密码。");
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const openInApp = () => {
    const hash = window.location.hash?.replace(/^#/, "") ?? "";
    // Carry the same recovery tokens into the app so supabase clients can `handle` it.
    window.location.href = `parentsmatch://login-callback#${hash}`;
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      // Best practice: sign out after password change, so the user re-auths with new password.
      await supabase.auth.signOut();
      alert("密码修改成功！请使用新密码登录。");
      navigate(WEB_AUTH_UI_ENABLED ? "/login" : "/");
    } catch (error: any) {
      alert("重置失败: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const showOpenAppButton =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) &&
    window.location.hash?.includes("type=recovery");

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h1 className="text-2xl font-bold mb-6 text-center text-gray-800">
          重置密码
        </h1>

        {status === "initializing" && (
          <p className="text-sm text-gray-600 text-center">正在加载重置链接…</p>
        )}

        {status === "expired" && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              链接已失效或未检测到重置会话，请重新申请重置密码。
            </p>
            <button
              onClick={() => navigate(WEB_AUTH_UI_ENABLED ? "/login" : "/")}
              className="w-full bg-gray-900 text-white py-2 rounded-lg font-bold hover:bg-gray-800 transition"
            >
              {WEB_AUTH_UI_ENABLED ? "去登录页重新发送" : "返回首页"}
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-3">
            <p className="text-sm text-red-600">初始化失败：{errorMsg}</p>
            <button
              onClick={() => navigate(WEB_AUTH_UI_ENABLED ? "/login" : "/")}
              className="w-full bg-gray-900 text-white py-2 rounded-lg font-bold hover:bg-gray-800 transition"
            >
              {WEB_AUTH_UI_ENABLED ? "去登录页重新发送" : "返回首页"}
            </button>
          </div>
        )}

        {status === "ready" && (
          <div className="space-y-4">
            {showOpenAppButton && (
              <button
                type="button"
                onClick={openInApp}
                className="w-full bg-white text-pink-700 py-2 rounded-lg font-bold hover:bg-pink-50 transition border border-pink-200"
              >
                在 App 中继续
              </button>
            )}

            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  新密码
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入新密码"
                  required
                  minLength={PASSWORD_MIN_LENGTH}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-pink-600 text-white py-2 rounded-lg font-bold hover:bg-pink-700 transition disabled:opacity-50"
              >
                {loading ? "提交中..." : "确认修改"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
